import React, { useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, Smartphone, KeyRound, Loader2, AlertTriangle, ShieldOff, QrCode, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * Challenge-näkymä jo enrolloituneille käyttäjille.
 * Sisältää myös "Unohditko Authenticatorin?" -dialogin self-reset-flowlle.
 */
export default function TwoFactorChallenge({ challengeToken, email, onCompleted, onCancel, onSelfReset }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  // Self-reset dialog state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState("");

  // Show-QR dialog state (lisää tili Authenticatoriin uudelleen)
  const [qrOpen, setQrOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrError, setQrError] = useState("");

  const fetchQrByChallenge = async () => {
    setQrError("");
    setQrLoading(true);
    setQrData(null);
    try {
      const r = await api.post("/auth/2fa/show-qr-by-challenge", {
        challenge_token: challengeToken,
      });
      setQrData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      let msg = formatApiError(e);
      if (status === 404 || msg === "Not Found") {
        msg = "Päivitä sivu (Ctrl+Shift+R) ja kirjaudu uudelleen — palvelin on päivittynyt.";
      } else if (status === 401) {
        msg = "Kirjautuminen vanhentui. Sulje tämä ikkuna, kirjaudu uudelleen ja klikkaa QR-nappia heti.";
      }
      setQrError(msg);
    }
    setQrLoading(false);
  };

  const openQrDialog = () => {
    setQrOpen(true);
    fetchQrByChallenge();
  };

  const closeQrDialog = (v) => {
    if (!v) {
      setQrOpen(false);
      setQrData(null);
      setQrError("");
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!code.trim()) return setError("Syötä koodi");
    setSubmitting(true);
    try {
      const r = await api.post("/auth/2fa/login", {
        challenge_token: challengeToken,
        code: code.trim(),
      });
      onCompleted?.(r.data);
    } catch (e) {
      setError(formatApiError(e));
    }
    setSubmitting(false);
  };

  const doSelfReset = async (e) => {
    e?.preventDefault?.();
    setResetError("");
    if (!resetPw || resetPw.length < 6) {
      return setResetError("Syötä salasanasi");
    }
    setResetting(true);
    try {
      const r = await api.post("/auth/2fa/self-reset", {
        email,
        password: resetPw,
      });
      setResetOpen(false);
      setResetPw("");
      // Käynnistä uudelleen setup-flow
      onSelfReset?.({
        setup_token: r.data.setup_token,
        email: r.data.email,
        full_name: r.data.full_name,
      });
    } catch (e) {
      setResetError(formatApiError(e));
    }
    setResetting(false);
  };

  return (
    <div className="app-bg flex items-center justify-center px-4 py-12 min-h-screen relative" data-testid="twofa-challenge">
      <div className="w-full max-w-md glass-strong p-8 space-y-5 relative z-10 animate-fade-in">
        <div className="text-center">
          <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-purple-600 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-8 h-8 text-white" />
            <div className="absolute -inset-1 rounded-2xl bg-amber-400/30 blur-md -z-10" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tietoturva</p>
          <h1 className="text-2xl font-display font-bold gradient-text-luxe">Vahvista 2FA-koodi</h1>
          <p className="text-xs text-white/50 mt-1">{email}</p>
        </div>

        {/* Onko Authenticator-sovellukseen lisätty tili? Näytä QR jos tarvitsee. */}
        <div className="rounded-xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-purple-500/10 p-4 space-y-2">
          <p className="text-sm font-semibold text-amber-100 flex items-center gap-2">
            <QrCode className="w-4 h-4" />
            Eikö tili ole vielä Authenticator-sovelluksessa?
          </p>
          <p className="text-xs text-white/70 leading-relaxed">
            Jos sinulla ei ole vielä "Apps2Go Hub" -merkintää Authenticator-sovelluksessa
            (esim. uusi puhelin), klikkaa alta — saat QR-koodin skannattavaksi.
            Sen jälkeen syötä alla 6-numeroinen koodi.
          </p>
          <Button
            type="button"
            onClick={openQrDialog}
            className="w-full btn-luxe inline-flex items-center justify-center gap-2"
            data-testid="twofa-challenge-show-qr"
          >
            <QrCode className="w-4 h-4" />
            Näytä QR-koodi ja lisää tili Authenticatoriin
          </Button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Label className="text-sm flex items-center gap-2 text-white/80">
            {useRecovery ? <KeyRound className="w-4 h-4 text-amber-300" /> : <Smartphone className="w-4 h-4 text-amber-300" />}
            {useRecovery ? "Syötä varakoodi" : "Syötä Authenticator-sovelluksen 6-numeroinen koodi"}
          </Label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={useRecovery ? "XXXX-XXXX-XXXX-XXXX" : "123 456"}
            maxLength={useRecovery ? 25 : 10}
            inputMode={useRecovery ? "text" : "numeric"}
            autoComplete="one-time-code"
            autoFocus
            className="text-center text-xl font-mono tracking-[0.5em] bg-black/30 border-white/10 h-12"
            data-testid="twofa-challenge-code"
          />
          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-200 leading-relaxed" data-testid="twofa-challenge-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="btn-luxe w-full inline-flex items-center justify-center gap-2"
            data-testid="twofa-challenge-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Vahvista ja kirjaudu
          </Button>
        </form>

        <div className="text-center text-xs space-y-2 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={() => {
              setUseRecovery((v) => !v);
              setCode("");
              setError("");
            }}
            className="text-amber-300 hover:text-amber-200 hover:underline"
            data-testid="twofa-toggle-recovery"
          >
            {useRecovery ? "← Käytä Authenticator-koodia" : "Käytä varakoodia →"}
          </button>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setResetOpen(true)}
              className="text-red-300 hover:text-red-200 hover:underline inline-flex items-center gap-1 justify-center"
              data-testid="twofa-self-reset"
            >
              <ShieldOff className="w-3 h-3" />
              Unohditko Authenticatorin / vanha tunniste rikki?
            </button>
            <button type="button" onClick={onCancel} className="text-white/40 hover:text-white/70">
              Peruuta ja palaa kirjautumiseen
            </button>
          </div>
        </div>
      </div>

      {/* QR-koodi: lisää tili Authenticator-sovellukseen login-vaiheessa */}
      <Dialog open={qrOpen} onOpenChange={closeQrDialog}>
        <DialogContent className="max-w-md" data-testid="twofa-challenge-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-amber-300" />
              Lisää tili Authenticatoriin
            </DialogTitle>
            <DialogDescription className="text-white/60 text-sm">
              Skannaa QR-koodi Google Authenticator-, Authy- tai vastaavalla sovelluksella.
              Sen jälkeen palaa tähän ikkunaan ja syötä sovelluksen 6-numeroinen koodi.
            </DialogDescription>
          </DialogHeader>

          {qrLoading && (
            <div className="flex items-center justify-center py-10 text-white/60 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Ladataan QR-koodia…
            </div>
          )}

          {qrError && !qrLoading && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-200">
              {qrError}
            </div>
          )}

          {qrData && !qrLoading && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-4 mx-auto w-fit shadow-2xl shadow-amber-500/20">
                <img
                  src={qrData.qr_data_url}
                  alt="2FA QR-koodi"
                  className="w-56 h-56 block"
                  data-testid="twofa-challenge-qr-image"
                />
              </div>
              <div className="text-center space-y-1">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tai syötä avain käsin</p>
                <div className="bg-black/40 rounded-lg px-3 py-3 border border-white/5">
                  <p
                    className="font-mono text-sm tracking-wider text-amber-200"
                    style={{ fontVariantNumeric: "slashed-zero tabular-nums" }}
                    data-testid="twofa-challenge-qr-secret"
                  >
                    {(qrData.secret.match(/.{1,4}/g) || []).join(" ")}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(qrData.secret);
                    toast.success("Avain kopioitu");
                  }}
                  className="text-xs gap-1 text-white/60 hover:text-white mt-2"
                >
                  <Copy className="w-3 h-3" /> Kopioi avain
                </Button>
              </div>
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100 flex gap-2 leading-relaxed">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-300" />
                <p>
                  <strong>Jos Authenticatorissa on jo "Apps2Go Hub" -merkintä:</strong> poista se ensin
                  ja skannaa tämä uusi QR-koodi sen tilalle, jotta vain yksi merkintä toimii.
                </p>
              </div>
              <Button
                onClick={() => closeQrDialog(false)}
                className="btn-luxe w-full inline-flex items-center justify-center gap-2"
                data-testid="twofa-challenge-qr-close"
              >
                <ShieldCheck className="w-4 h-4" />
                Tili lisätty – jatka koodin syöttöön
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Self-reset dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md" data-testid="twofa-self-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-200">
              <ShieldOff className="w-5 h-5" /> Nollaa 2FA ja luo uusi
            </DialogTitle>
            <DialogDescription className="text-white/60 text-sm">
              Tämä poistaa nykyisen Authenticator-yhteyden ja antaa sinulle uuden QR-koodin
              skannattavaksi. Käytä tätä jos sinulla on useita "Apps2Go Hub" -merkintöjä tai
              menetit pääsyn vanhaan merkintään.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-100 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              <strong>Ennen kuin jatkat:</strong> Avaa Authenticator-sovelluksesi ja{" "}
              <strong>poista kaikki "Apps2Go Hub" -merkinnät</strong>. Sen jälkeen palaa tähän
              ja vahvista salasanallasi.
            </p>
          </div>
          <form onSubmit={doSelfReset} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60">Salasanasi</Label>
              <Input
                type="password"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                placeholder="Syötä salasanasi vahvistukseksi"
                className="bg-black/30 border-white/10 mt-1"
                data-testid="twofa-self-reset-password"
                autoFocus
              />
            </div>
            {resetError && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-2 text-xs text-red-200">
                {resetError}
              </div>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => setResetOpen(false)} className="flex-1">
                Peruuta
              </Button>
              <Button
                type="submit"
                disabled={resetting}
                className="flex-1 bg-red-500 hover:bg-red-400 text-white inline-flex items-center justify-center gap-2"
                data-testid="twofa-self-reset-confirm"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                Nollaa 2FA
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
